import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const followUpSequenceApi = createApi({
  reducerPath: "followUpSequenceApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FollowUpSequence", "SequenceStats", "DueActions", "Templates"],
  endpoints: (builder) => ({
    // Get all sequences
    getAllSequences: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.category) queryParams.append("category", params.category);
        if (params.search) queryParams.append("search", params.search);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        return `/follow-up-sequences?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "FollowUpSequence", id: _id })),
              { type: "FollowUpSequence", id: "LIST" },
            ]
          : [{ type: "FollowUpSequence", id: "LIST" }],
    }),

    // Get single sequence
    getSequenceById: builder.query({
      query: (id) => `/follow-up-sequences/${id}`,
      providesTags: (result, error, id) => [{ type: "FollowUpSequence", id }],
    }),

    // Create sequence
    createSequence: builder.mutation({
      query: (sequenceData) => ({
        url: "/follow-up-sequences",
        method: "POST",
        body: sequenceData,
      }),
      invalidatesTags: [
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
      ],
    }),

    // Update sequence
    updateSequence: builder.mutation({
      query: ({ id, ...sequenceData }) => ({
        url: `/follow-up-sequences/${id}`,
        method: "PUT",
        body: sequenceData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FollowUpSequence", id },
        { type: "FollowUpSequence", id: "LIST" },
      ],
    }),

    // Delete sequence
    deleteSequence: builder.mutation({
      query: (id) => ({
        url: `/follow-up-sequences/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
      ],
    }),

    // Toggle sequence status
    toggleSequenceStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/follow-up-sequences/${id}/toggle-status`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FollowUpSequence", id },
        { type: "FollowUpSequence", id: "LIST" },
      ],
    }),

    // Duplicate sequence
    duplicateSequence: builder.mutation({
      query: ({ id, name }) => ({
        url: `/follow-up-sequences/${id}/duplicate`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: [
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
      ],
    }),

    // Enroll client
    enrollClient: builder.mutation({
      query: ({ sequenceId, ...clientData }) => ({
        url: `/follow-up-sequences/${sequenceId}/enroll`,
        method: "POST",
        body: clientData,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: "FollowUpSequence", id: sequenceId },
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
        { type: "DueActions" },
      ],
    }),

    // Advance client
    advanceClient: builder.mutation({
      query: ({ sequenceId, enrollmentId, notes }) => ({
        url: `/follow-up-sequences/${sequenceId}/client/${enrollmentId}/advance`,
        method: "POST",
        body: { notes },
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: "FollowUpSequence", id: sequenceId },
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
        { type: "DueActions" },
      ],
    }),

    // Remove client
    removeClient: builder.mutation({
      query: ({ sequenceId, enrollmentId }) => ({
        url: `/follow-up-sequences/${sequenceId}/client/${enrollmentId}/remove`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: "FollowUpSequence", id: sequenceId },
        { type: "SequenceStats" },
        { type: "DueActions" },
      ],
    }),

    // Toggle client status
    toggleClientStatus: builder.mutation({
      query: ({ sequenceId, enrollmentId, status }) => ({
        url: `/follow-up-sequences/${sequenceId}/client/${enrollmentId}/toggle`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: "FollowUpSequence", id: sequenceId },
        { type: "DueActions" },
      ],
    }),

    // Get stats
    getSequenceStats: builder.query({
      query: () => "/follow-up-sequences/stats",
      providesTags: [{ type: "SequenceStats" }],
    }),

    // Get due actions
    getDueActions: builder.query({
      query: (days = 7) => `/follow-up-sequences/due-actions?days=${days}`,
      providesTags: [{ type: "DueActions" }],
    }),

    // Get templates
    getTemplates: builder.query({
      query: () => "/follow-up-sequences/templates",
      providesTags: [{ type: "Templates" }],
    }),

    // Create from template
    createFromTemplate: builder.mutation({
      query: (templateId) => ({
        url: "/follow-up-sequences/from-template",
        method: "POST",
        body: { templateId },
      }),
      invalidatesTags: [
        { type: "FollowUpSequence", id: "LIST" },
        { type: "SequenceStats" },
      ],
    }),
  }),
});

export const {
  useGetAllSequencesQuery,
  useGetSequenceByIdQuery,
  useCreateSequenceMutation,
  useUpdateSequenceMutation,
  useDeleteSequenceMutation,
  useToggleSequenceStatusMutation,
  useDuplicateSequenceMutation,
  useEnrollClientMutation,
  useAdvanceClientMutation,
  useRemoveClientMutation,
  useToggleClientStatusMutation,
  useGetSequenceStatsQuery,
  useGetDueActionsQuery,
  useGetTemplatesQuery,
  useCreateFromTemplateMutation,
} = followUpSequenceApi;
